import {
	Component,
	forwardRef,
	AfterViewInit,
	ViewChild,
	Input,
} from '@angular/core';
import { FieldWorkerUnavailableDay, Division } from 'src/shared/api';
import { unavailableDayFormConfig } from '../unavailable-day.component';
import { UnavailableDayDialogComponent } from '../unavailable-day-dialog/unavailable-day-dialog.component';
import { animateList } from 'src/shared/animations';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormGroup,
	FormArray,
	FormBuilder,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbstractControlDataSource } from '../../abstract-control-data-source';
import { takeUntil, filter } from 'rxjs/operators';
import { UnavailableMultipleDayDialogComponent } from '../unavailable-multiple-day-dialog/unavailable-multiple-day-dialog.component';
import * as moment from 'moment';
import { UnavailableMultiDeleteDialogComponent } from '../unavailable-multi-delete-dialog/unavailable-multi-delete-dialog.component';
import { dateParamFormat } from 'src/shared/constants';

/*
	This table component is built using a form array to allow for easier editing.
	The important thing to remember is that the "parent" FormGroup is just a wrapper
	and has no actual value outside of giving us somewhere to store a FormArray. As of
	Angular 7 (and probably 8), there is no way to have a FormArray be the top level
	of a form.
*/

@Component({
	selector: 'rpshared-unavailable-day-list',
	templateUrl: './unavailable-day-list.component.html',
	styleUrls: ['./unavailable-day-list.component.css'],
	animations: [animateList],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => UnavailableDayListComponent),
			multi: true,
		},
	],
})
export class UnavailableDayListComponent extends SelfDestruct
	implements ControlValueAccessor, AfterViewInit {
	@Input() fieldWorkerId = 0;

	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	emptyColumns = ['empty'];
	resultColumns = ['date', 'reason', 'edit', 'delete'];

	// Form Controls
	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<FieldWorkerUnavailableDay>(
		this.array
	);

	constructor(
		private readonly dialog: MatDialog,
		private readonly fb: FormBuilder
	) {
		super();
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: FieldWorkerUnavailableDay[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(unavailableDayFormConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: FieldWorkerUnavailableDay[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addDay() {
		this.dialog
			.open<UnavailableDayDialogComponent, never, FormGroup>(
				UnavailableDayDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				control.patchValue({ fieldWorker: { id: this.fieldWorkerId } });
				(this.parent.get('array') as FormArray).insert(0, control);
			});
	}

	addMultipleDays() {
		this.dialog
			.open<UnavailableMultipleDayDialogComponent, never, FormGroup>(
				UnavailableMultipleDayDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				control.patchValue({ fieldWorker: { id: this.fieldWorkerId } });
				this.processMultipleAdd(control);
			});
	}

	private processMultipleAdd(control: FormGroup) {
		const startDate = moment.utc(control.value.startDate, dateParamFormat);
		const endDate = moment.utc(control.value.endDate, dateParamFormat);

		let currentDate = startDate;

		while (currentDate <= endDate) {
			(this.parent.get('array') as FormArray).insert(
				0,
				this.fb.group({
					id: [control.value.id],
					fieldWorker: [control.value.fieldWorker],
					date: [moment.utc(currentDate, dateParamFormat)],
					reason: [control.value.reason],
				})
			);
			currentDate = currentDate.add(1, 'd');
		}
	}

	deleteMultipleDays() {
		this.dialog
			.open<UnavailableMultiDeleteDialogComponent, never, FormGroup>(
				UnavailableMultiDeleteDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				this.processMultipleDelete(control);
			});
	}

	private processMultipleDelete(control: FormGroup) {
		const array = this.parent.get('array') as FormArray;
		const startDate = moment.utc(control.value.startDate, dateParamFormat);
		const endDate = moment.utc(control.value.endDate, dateParamFormat);
		let currentDate = startDate;

		while (currentDate <= endDate) {
			array.removeAt(
				array.value.findIndex((ctrl: any) =>
					moment(ctrl.date).isSame(currentDate)
				)
			);
			currentDate = currentDate.add(1, 'd');
		}
	}


	editDay(day: FieldWorkerUnavailableDay) {
		this.dialog
			.open<UnavailableDayDialogComponent, Division, FormGroup>(
				UnavailableDayDialogComponent,
				{
					width: '450px',
					data: day
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				(this.parent.get('array') as FormArray).setControl(
					this.findDayIndex(day),
					control
				);
			});
	}

	findDayIndex(day: FieldWorkerUnavailableDay){
		return this.dataSource.data.findIndex(d => d === day);
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}
}
